import { useState } from 'react';

function generateId(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
}

/**
 * Clean up a class id the officer TYPED, preserving its case (ra-15xp).
 *
 * A class id is an exact-case FK between `season.classes` (what the app offers
 * for registration) and `fleet_config.classes` (what the schedule links to) —
 * see `diffSeasonFleetClasses`, where a case-only difference is real divergence
 * because the FK genuinely fails to resolve. Lower-casing here silently broke
 * that link for any id whose real spelling is mixed-case (`J109` -> `j109`),
 * including one adopted verbatim from season data by the name match above.
 * Ids generated from a name stay lower-case slugs; typed ids keep their case.
 */
function sanitizeClassId(value) {
  return String(value ?? '').replace(/[^A-Za-z0-9_]/g, '');
}

function findExistingClass(name, existingClasses) {
  const needle = name.trim().toLowerCase();
  return (existingClasses || []).find((c) => c.name && c.name.toLowerCase() === needle);
}

export default function ClassEditor({ classes, fleets, startLocations, existingClasses, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ name: '', id: '', flag: '', fleet_id: '', start_location_id: '' });
  const [idSource, setIdSource] = useState(null); // 'season' | 'generated' | 'manual'

  const updateDraftName = (name) => {
    const match = findExistingClass(name, existingClasses);
    if (match) {
      setDraft((d) => ({ ...d, name, id: match.id }));
      setIdSource('season');
    } else {
      setDraft((d) => ({ ...d, name, id: generateId(name) }));
      setIdSource(name.trim() ? 'generated' : null);
    }
  };

  const addClass = () => {
    if (!draft.name.trim()) return;
    const id = draft.id.trim() || generateId(draft.name);
    if (classes.some((c) => c.id === id)) return;
    const entry = {
      id,
      name: draft.name.trim(),
      flag: draft.flag.trim(),
    };
    if (draft.fleet_id) entry.fleet_id = draft.fleet_id;
    if (draft.start_location_id) entry.start_location_id = draft.start_location_id;
    onChange([...classes, entry]);
    setDraft({ name: '', id: '', flag: '', fleet_id: '', start_location_id: '' });
    setIdSource(null);
    setAdding(false);
  };

  const removeClass = (id) => {
    onChange(classes.filter((c) => c.id !== id));
  };

  const updateClass = (id, field, value) => {
    onChange(classes.map((c) => {
      if (c.id !== id) return c;
      const next = { ...c };
      // Clearing a field (e.g. "—" start location) must DELETE the key, not set
      // it to `undefined` — an undefined value survives the object spread into the
      // fleet_config setDoc and Firestore rejects it. Clear only on empty-string /
      // null so falsy-but-valid values (0, false) are preserved, not dropped.
      if (value === '' || value == null) delete next[field];
      else next[field] = value;
      return next;
    }));
  };

  const moveClass = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= classes.length) return;
    const next = [...classes];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  const fleetName = (fleetId) => {
    const f = fleets.find((fl) => fl.id === fleetId);
    return f ? f.name : '';
  };

  const locationName = (locId) => {
    const l = startLocations.find((loc) => loc.id === locId);
    return l ? l.name : '';
  };

  return (
    <section className="config-section">
      <h2>Classes</h2>
      <p className="section-hint">
        Define boat classes with their flag codes. Optionally assign each class to a fleet and start location.
      </p>

      {classes.length === 0 && !adding && (
        <p className="empty-state">No classes defined yet.</p>
      )}

      {classes.length > 0 && (
        <table className="config-table">
          <thead>
            <tr>
              <th style={{ width: '3rem' }}>Order</th>
              <th>Name</th>
              <th>ID</th>
              <th>Flag</th>
              <th>Fleet</th>
              <th>Start Location</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {classes.map((cls, idx) => (
              <tr key={cls.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    className="btn btn-sm"
                    style={{ padding: '0.1rem 0.3rem', fontSize: '0.7rem' }}
                    disabled={idx === 0}
                    onClick={() => moveClass(idx, -1)}
                    title="Move up"
                  >&uarr;</button>
                  <button
                    className="btn btn-sm"
                    style={{ padding: '0.1rem 0.3rem', fontSize: '0.7rem', marginLeft: '0.15rem' }}
                    disabled={idx === classes.length - 1}
                    onClick={() => moveClass(idx, 1)}
                    title="Move down"
                  >&darr;</button>
                </td>
                <td>
                  <input
                    type="text"
                    value={cls.name}
                    onChange={(e) => updateClass(cls.id, 'name', e.target.value)}
                    className="inline-input"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    defaultValue={cls.id}
                    onBlur={(e) => {
                      const newId = sanitizeClassId(e.target.value);
                      if (!newId || newId === cls.id) { e.target.value = cls.id; return; }
                      if (classes.some((c) => c.id === newId)) { e.target.value = cls.id; return; }
                      onChange(classes.map((c) => (c.id === cls.id ? { ...c, id: newId } : c)));
                    }}
                    className="inline-input"
                    style={{ fontFamily: 'monospace', fontSize: '0.8rem', width: '8rem' }}
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={cls.flag || ''}
                    onChange={(e) => updateClass(cls.id, 'flag', e.target.value)}
                    className="inline-input flag-input"
                    placeholder="e.g. No. 2"
                  />
                </td>
                <td>
                  <select
                    value={cls.fleet_id || ''}
                    onChange={(e) => updateClass(cls.id, 'fleet_id', e.target.value)}
                    className="inline-select"
                  >
                    <option value="">—</option>
                    {fleets.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={cls.start_location_id || ''}
                    onChange={(e) => updateClass(cls.id, 'start_location_id', e.target.value)}
                    className="inline-select"
                  >
                    <option value="">—</option>
                    {startLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <button className="btn btn-danger btn-sm" onClick={() => removeClass(cls.id)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding ? (
        <div className="add-form">
          <div className="form-row">
            <input
              type="text"
              placeholder="Class name (e.g. Cruisers 2)"
              value={draft.name}
              onChange={(e) => updateDraftName(e.target.value)}
              className="inline-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && addClass()}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
              <input
                type="text"
                placeholder="ID"
                value={draft.id}
                onChange={(e) => {
                  setDraft({ ...draft, id: sanitizeClassId(e.target.value) });
                  setIdSource('manual');
                }}
                className="inline-input"
                style={{ fontFamily: 'monospace', fontSize: '0.8rem', width: '8rem' }}
              />
              {idSource === 'season' && (
                <span style={{ fontSize: '0.7rem', color: '#27ae60' }} title="Matched from season data">&#10003; season</span>
              )}
              {idSource === 'generated' && (
                <span style={{ fontSize: '0.7rem', color: '#e67e22' }} title="New ID — not found in any season">new</span>
              )}
            </span>
            <input
              type="text"
              placeholder="Flag (e.g. No. 2)"
              value={draft.flag}
              onChange={(e) => setDraft({ ...draft, flag: e.target.value })}
              className="inline-input flag-input"
            />
            <select
              value={draft.fleet_id}
              onChange={(e) => setDraft({ ...draft, fleet_id: e.target.value })}
              className="inline-select"
            >
              <option value="">Fleet (optional)</option>
              {fleets.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
            <select
              value={draft.start_location_id}
              onChange={(e) => setDraft({ ...draft, start_location_id: e.target.value })}
              className="inline-select"
            >
              <option value="">Location (optional)</option>
              {startLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
            <button className="btn btn-primary btn-sm" onClick={addClass}>Add</button>
            <button className="btn btn-sm" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <button className="btn btn-sm" onClick={() => setAdding(true)}>+ Add Class</button>
      )}
    </section>
  );
}
